/**
 * Enum values mirrored from `@universal-jewellery/universal-packages`. Hardcoded so the SPA stays
 * dependency-light (the shared package is a private GitHub Packages dep with server-side reach);
 * if it gains a value, add it here. Each list names its source file so the mirror is checkable.
 *
 * These are not cosmetic. products-api casts `cut` / `stones` / `colours` / `excludeProductType`
 * straight into Prisma with NO runtime validation — one bad value is a 500, not a 4xx. Driving the
 * filter controls from these lists is what makes an invalid value unsendable by construction.
 */

/** MetalEnum — shared/metals.ts. The bench's target-metal selector offers all of them, so a tester
 *  can pick a metal the ring isn't catalogued in and probe an out-of-catalogue swap. */
export const KNOWN_METALS = [
  "METAL_YELLOW_GOLD_18K",
  "METAL_ROSE_GOLD_18K",
  "METAL_WHITE_GOLD_18K",
  "METAL_YELLOW_GOLD_14K",
  "METAL_ROSE_GOLD_14K",
  "METAL_WHITE_GOLD_14K",
  "METAL_YELLOW_GOLD_9K",
  "METAL_ROSE_GOLD_9K",
  "METAL_WHITE_GOLD_9K",
  "METAL_GREY_GOLD_18K",
  "METAL_GREEN_GOLD_18K",
  "METAL_PLATINUM_950",
] as const;

/** A metal enum rendered for the bench's swatch picker: a colour family, a karat/fineness badge,
 *  and the CSS class of the metal bead — so a tester picks a metal by seeing it, not by reading
 *  an enum string. Pure. */
export interface MetalDisplay {
  /** Human family name, e.g. "Yellow", "Platinum". */
  family: string;
  /** Fineness badge, e.g. "18K", "9K", "950". */
  karat: string;
  /** Bead gradient class in styles.css (`m-yellow`, `m-rose`, …, `m-plat`). */
  swatchClass: string;
}

const FAMILY_TO_SWATCH: Record<string, string> = {
  YELLOW: "m-yellow",
  ROSE: "m-rose",
  WHITE: "m-white",
  GREY: "m-grey",
  GREEN: "m-green",
  PLATINUM: "m-plat",
};

/** Parse a `METAL_<FAMILY>_GOLD_<KARAT>` / `METAL_PLATINUM_<FINENESS>` enum into swatch parts.
 *  Unknown families fall back to a neutral white-metal bead so the picker never renders blank. */
export function metalDisplay(metal: string): MetalDisplay {
  const parts = metal.replace(/^METAL_/, "").split("_"); // ["YELLOW","GOLD","18K"] | ["PLATINUM","950"]
  const familyKey = parts[0] ?? "";
  const karat = parts[parts.length - 1] ?? "";
  const family = familyKey.charAt(0) + familyKey.slice(1).toLowerCase();
  return { family, karat, swatchClass: FAMILY_TO_SWATCH[familyKey] ?? "m-white" };
}

/** CutEnum — shared/cut.ts. products-api calls the shape `cut`; ring-swap calls it `shape`.
 *  `BRILLIANT` and `ROUND` are two distinct members, and `UNSPECIFIED` is a real stored value. */
export const CUTS = [
  "ROUND",
  "OVAL",
  "PRINCESS",
  "EMERALD",
  "ASSCHER",
  "CUSHION",
  "RADIANT",
  "MARQUISE",
  "PEAR",
  "HEART",
  "BAGUETTE",
  "TAPERED_BAGUETTE",
  "TRILLIANT",
  "TRIANGLE",
  "TRAPEZOID",
  "HALFMOON",
  "KITE",
  "COFFIN",
  "CADILLAC",
  "BULLET",
  "OCTAGON",
  "HEXAGON",
  "BRILLIANT",
  "UNSPECIFIED",
] as const;

/** GemstoneEnum — shared/gemstones.ts. */
export const GEMSTONE_TYPES = [
  "DIAMOND",
  "MOISSANITE",
  "SAPPHIRE",
  "RUBY",
  "EMERALD",
  "AQUAMARINE",
  "ALEXANDRITE",
  "TOURMALINE",
  "SPINEL",
  "GARNET",
  "AMETHYST",
  "OPAL",
  "MOONSTONE",
] as const;

/** ClarityEnum — shared/clarity.ts. Ordered best→worst, which is how the trade reads them. */
export const CLARITIES = ["IF", "VVS", "VVS1", "VVS2", "VS", "VS1", "VS2", "SI", "SI1", "SI2", "I1", "I2", "I3", "UNSPECIFIED"] as const;

/** ColourGradeEnum — shared/colour-grade.ts. `E_F` / `F_G` are real composite grades. */
export const COLOUR_GRADES = [
  "D",
  "E",
  "E_F",
  "F",
  "F_G",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "UNSPECIFIED",
] as const;

/** ColoursEnum — shared/colours.ts. The fancy-colour branch (ADR-008); empty on Nivoda stones. */
export const COLOURS = [
  "COLOURLESS",
  "WHITE",
  "BLACK",
  "GREY",
  "BLUE",
  "GREEN",
  "TEAL",
  "YELLOW",
  "ORANGE",
  "PEACH",
  "PINK",
  "RED",
  "PURPLE",
  "BROWN",
  "CHAMPAGNE",
  "BI_COLOUR",
  "PARTI_COLOUR",
  "COLOUR_CHANGE",
  "CHAMELEON",
  "OTHER",
] as const;

/** CertificationLabEnum — shared/certification-lab.ts. Only ever populated on Nivoda stones. */
export const CERT_LABS = ["GIA", "IGI", "GCAL", "AGS", "HRD", "EGL", "WISE", "OTHER"] as const;

/** AudienceEnum — shared/audience.ts. */
export const AUDIENCES = ["WOMENS", "MENS", "UNISEX"] as const;

/** StoneSourceEnum — shared/source-enum.ts. Decides which attributes a stone can even carry:
 *  INTERNAL never has certification/treatment/origin; NIVODA always has `colours: []`. */
export const STONE_SOURCES = ["INTERNAL", "NIVODA"] as const;

/** ProductStockFiltersEnum — dto/pagination/products/product-pagination.dto.ts. */
export const STOCK_STATUSES = ["IN_STOCK", "OUT_OF_STOCK", "ALL"] as const;

/**
 * RING_PRODUCT_TYPES — product-type.ts `RING_PRODUCT_TYPES`. All five types products-api resolves a
 * `ringProduct` for. The bench previously listed only the first three, which silently hid every
 * wedding band (69 of them in the local catalogue) from the specimen search.
 */
export const RING_PRODUCT_TYPES = ["PRE_MADE_RING", "MADE_TO_ORDER_RING", "CUSTOM_RING", "PRE_MADE_WEDDING_BAND", "MADE_TO_ORDER_WEDDING_BAND"] as const;

/**
 * products-api stores "we don't know" as a value, not as null: `UNSPECIFIED` for clarity/colour
 * grade/cut, `""` for fancy colour and supplier reference. Rendering those verbatim would state a
 * non-fact ("Clarity: UNSPECIFIED"), so every display path filters through this first.
 */
export function isRecorded(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return true;
  const v = value.trim();
  return v !== "" && v !== "UNSPECIFIED";
}

const LABEL_OVERRIDES: Record<string, string> = {
  E_F: "E–F",
  F_G: "F–G",
  BI_COLOUR: "Bi-colour",
  PARTI_COLOUR: "Parti-colour",
  COLOUR_CHANGE: "Colour-change",
  PRE_MADE_RING: "Pre-made ring",
  MADE_TO_ORDER_RING: "Made-to-order ring",
  CUSTOM_RING: "Custom ring",
  PRE_MADE_WEDDING_BAND: "Pre-made wedding band",
  MADE_TO_ORDER_WEDDING_BAND: "Made-to-order wedding band",
  TAPERED_BAGUETTE: "Tapered baguette",
  IN_STOCK: "In stock",
  OUT_OF_STOCK: "Out of stock",
  ALL: "All",
};

/**
 * Values that are already the trade's own words and must never be title-cased: `VVS1` is not
 * "Vvs1", `D` is not "D" by accident, `GIA` is not "Gia". Membership, not a shape test — a pattern
 * like /^[A-Z]{1,4}$/ also swallows RUBY, OPAL, TEAL, PINK and MENS, which then SHOUT next to their
 * title-cased 5-letter siblings ("TEAL · Green · Parti-colour").
 */
const VERBATIM = new Set<string>([...CLARITIES, ...COLOUR_GRADES, ...CERT_LABS]);
// Plain English words that happen to live in those enums — they read as prose, not as grades.
// "OTHER" is a member of ColoursEnum *and* CertificationLabEnum, so leaving it in would render a
// sapphire's colour as "Pink · OTHER".
for (const word of ["OTHER", "UNSPECIFIED"]) VERBATIM.delete(word);

/**
 * Humanise an enum value for prose (the detail sheet). Filter controls deliberately skip this and
 * show the raw value: on a test bench the wire value is the useful one.
 */
export function enumLabel(value: string): string {
  const override = LABEL_OVERRIDES[value];
  if (override) return override;
  if (VERBATIM.has(value)) return value;
  const spaced = value.replace(/_/g, " ").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
